import { copy } from "angular";
import * as _ from "lodash";
import { isNullOrUndefined } from "util";

import { TradingRepo as tradingRepo } from "../../trading/trading.repo";
import { PortfolioRepo as portfolioRepo } from "../../portfolio/portfolio.repo";
import { Trade } from "../../trading/models/trade";
import { TradeSet as TradeSet } from "../../trading/models/tradeSet";
import { TradeRestriction } from "../../trading/models/tradeRestriction";
import { TradeRecommendation } from "../../trading/models/tradeRecommendation";
import {
  AssetClassNames,
  AssetClassColour,
  AssetClassColourArray,
  AssetClassNameArray,
  FixedIncomeTypeNames,
} from "../../common/enums/AssetClassType";
import { Locality } from "../../cmodel/cmodel.repo";
import { SecurityDetails } from "../../simulations/models/securityDetails";
import { ExchangeService } from "../../common/services/exchange.service";

import { trunc } from "../../common/utils/trunc";

class TradeRecommendationsController
  implements angular.IOnInit, angular.IOnChanges
{
  // #region Input from parent
  public clientPortfolios: any[];
  public selectedPortfolioIds: number[]; // Not used
  public selectedCurrency: string;
  public clientNumber: string; // Not used
  public clientId: number;
  // #endregion

  public tradeLocality = Locality.Local;
  public portfolioId: number;
  public activePortfolio: any;
  public displayCurrency: string;
  public portfolioTotal: number;
  public isLoading = false;

  private modelChange = true;
  private tradeSetSnapshot: TradeSet;
  private activeSimulation = false;

  public tradeSet: TradeSet;
  public tradeRecommendations: Array<TradeRecommendation>;
  public cashItems: Array<any>;

  public recommendationGroups = [
    AssetClassNames.EQUITY,
    FixedIncomeTypeNames.BOND,
    FixedIncomeTypeNames.FUND,
    FixedIncomeTypeNames.DEPOSIT,
    AssetClassNames.PRECIOUS_METALS,
    AssetClassNames.ALTERNATIVE_INV,
  ];
  public recommendationGrouping = {
    [AssetClassNames.EQUITY]: [],
    [FixedIncomeTypeNames.BOND]: [],
    [FixedIncomeTypeNames.FUND]: [],
    [FixedIncomeTypeNames.DEPOSIT]: [],
    [AssetClassNames.PRECIOUS_METALS]: [],
    [AssetClassNames.ALTERNATIVE_INV]: [],
  };

  // actual fields
  public assetClass: any[];
  public actualModelTotal: number;
  public assetClassesTotal: number;

  // target fields
  public targetAssetClass: any[];
  public targetAssetClassesTotal: number;
  public targetModelTotal: number;
  public totalBuy = 0;
  public totalSell = 0;

  // ideal fields
  public idealAssetClass: any[];
  public idealAssetClassesTotal: number;
  public idealModelTotal: number;

  public cModelTotal: number;

  public get showLocalitySelector() {
    if (!!this.tradeSet && this.tradeSet.tradeSetId !== 0) {
      return false;
    }

    if (!this.tradeRecommendations) {
      return false;
    }
    return (
      this.tradeRecommendations.some(
        (t) => t.security.locality === Locality.Local
      ) &&
      this.tradeRecommendations.some(
        (t) => t.security.locality === Locality.International
      )
    );
  }

  // colour array
  public colourArray = AssetClassColourArray;

  /** @ngInject */
  constructor(
    private tradingRepo: tradingRepo,
    private portfolioRepo: portfolioRepo,
    private $mdDialog: angular.material.IDialogService,
    private $scope: angular.IScope,
    private $rootScope: angular.IRootScopeService,
    private notifyjs: any,
    public exchangeService: ExchangeService
  ) {}

  public getIdealAssetClassPercentage(assetClassTotal: number) {
    return trunc((assetClassTotal / this.idealAssetClassesTotal) * 100, 2);
  }

  $onInit() {
    this.displayCurrency = this.selectedCurrency;
    this.onPortfolioSelect(this.clientPortfolios[0]);
  }

  $onChanges(onChangesObj: angular.IOnChangesObject): void {
    if (
      !this.activeSimulation &&
      !onChangesObj.selectedCurrency.isFirstChange() &&
      onChangesObj.selectedCurrency.currentValue !==
        onChangesObj.selectedCurrency.previousValue
    ) {
      this.displayCurrency = onChangesObj.selectedCurrency.currentValue;
      this.onPortfolioSelect(this.activePortfolio);
    }
  }

  public onPortfolioSelect(portfolio: any) {
    this.isLoading = true;
    if (this.activePortfolio) {
      this.activePortfolio.active = false;
    }

    this.activePortfolio = portfolio;
    portfolio.active = true;

    this.portfolioId = portfolio.id;

    this.tradeSetSnapshot = null;

    Promise.all([this.updateCashItems(), this.getLatestTrades()])
      // Will be called as a finally for both requests
      .then(() => {
        if (this.cashItems && this.tradeRecommendations) {
          this.updateAllCharts();
        }
      })
      .then(() => {
        this.isLoading = false;
      });
  }

  public getLatestTrades() {
    this.tradeSet = null;
    this.tradeRecommendations = null;
    this.tradeLocality = Locality.Local;
    this.activeSimulation = false;

    return this.tradingRepo
      .getLatestTrades(this.clientId, this.portfolioId, this.selectedCurrency)
      .then((r) => {
        var object = r.data;
        if (object) {
          this.tradeSetSnapshot = new TradeSet(object);
          this.tradeSet = copy(this.tradeSetSnapshot);
          this.tradeRecommendations = this.tradeSet.trades.map(
            (t) => new TradeRecommendation(t)
          );

          if (
            this.tradeRecommendations.every(
              (t) => t.security.locality === Locality.International
            ) ||
            this.tradeRecommendations
              .filter((t) => !!t.buySellQuantity)
              .every((t) => t.security.locality === Locality.International)
          ) {
            this.tradeLocality = Locality.International;
          }
        }
      })
      .then(() => {
        this.groupTradeRecommendation.bind(this);
      })
      .catch((err) => {
        this.isLoading = false;
        this.notifyjs(err.data);
      });
  }

  public groupTradeRecommendation() {
    this.recommendationGrouping = {
      [AssetClassNames.EQUITY]: [],
      [FixedIncomeTypeNames.BOND]: [],
      [FixedIncomeTypeNames.FUND]: [],
      [FixedIncomeTypeNames.DEPOSIT]: [],
      [AssetClassNames.PRECIOUS_METALS]: [],
      [AssetClassNames.ALTERNATIVE_INV]: [],
    };
    this.tradeRecommendations.forEach((tr) => {
      if (this.recommendationGrouping[tr.security.assetClass.name]) {
        this.recommendationGrouping[tr.security.assetClass.name].push(tr);
      } else if (
        tr.security.assetClass.name === AssetClassNames.FIXED_INCOME &&
        tr.security.fixedIncomeType &&
        this.recommendationGrouping[tr.security.fixedIncomeType.name]
      ) {
        this.recommendationGrouping[tr.security.fixedIncomeType.name].push(tr);
      }
    });
  }

  public updateCashItems() {
    this.cashItems = null;
    return this.portfolioRepo
      .getPortfolioCashItemsForAdmin(
        this.clientId,
        [this.portfolioId],
        this.selectedCurrency
      )
      .then((r) => (this.cashItems = r.data as Array<any>))
      .catch((err) => this.notifyjs(err.data));
  }

  public showActiveTradeDetails(securityId: number) {
    this.tradingRepo
      .getActiveTradeDetails(securityId, this.portfolioId)
      .then((resp) => {
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.tradeDetails = resp.data;

        return this.$mdDialog.show({
          template:
            '<trade-details-modal trade-details="tradeDetails"></trade-details-modal>',
          scope: tempScope,
        });
      });
  }

  checkSecurityTrades(securityId: number, portfolio: TradeRecommendation) {
    let returnValue = false;
    let totalTradeQuantity = 0;
    this.tradingRepo
      .getActiveTradeDetails(securityId, this.portfolioId)
      .then((resp) => {
        let trades = resp.data as any;
        trades.activeTrades.forEach((element) => {
          if (element.quantityRecovered !== Math.abs(element.buySellQuantity)) {
            if (element.buySellQuantity < 0) {
              totalTradeQuantity += Math.abs(element.buySellQuantity);
            }
          }
        });
        if (portfolio.buySellQuantity < 0) {
          if (totalTradeQuantity > 0) {
            if (
              Math.abs(portfolio.quantity) - Math.abs(totalTradeQuantity) ==
              0
            ) {
              returnValue = true;
            } else if (
              Math.abs(portfolio.quantity) - Math.abs(totalTradeQuantity) <
              Math.abs(portfolio.buySellQuantity)
            ) {
              returnValue = true;
            }
          }
        }

        portfolio.sellAllowed = returnValue;
      });
  }

  private updateAllCharts() {
    this.updateActualCharts();
    this.updateTargetCharts();
    this.updateIdealCharts();
  }

  public generateRecommendations() {
    this.displayCurrency = this.selectedCurrency;
    this.modelChange = true;
    this.isLoading = true;
    this.tradeRecommendations = null;
    this.tradeLocality = Locality.Local;

    const recommendationPromise = this.tradingRepo
      .getRecommendations(this.clientId, this.portfolioId, this.displayCurrency)
      .then((r) => {
        var object = r.data;
        if (object != null) {
          this.tradeSetSnapshot = new TradeSet(object);
          this.tradeSet = copy(this.tradeSetSnapshot);
          this.tradeRecommendations = this.tradeSet.trades.map(
            (t) => new TradeRecommendation(t)
          );

          if (
            this.tradeRecommendations.every(
              (t) => t.security.locality === Locality.International
            )
          ) {
            this.tradeLocality = Locality.International;
          }

          this.activeSimulation = true;
        }
      })
      .then(this.groupTradeRecommendation.bind(this))
      .catch((err) => {
        console.log(err);
        this.notifyjs(err.data);
      });

    Promise.all([this.updateCashItems(), recommendationPromise])
      // Will be called as a finally for both requests
      .then(() => {
        if (this.cashItems && this.tradeRecommendations) {
          this.updateAllCharts();
          this.updatePercentages();
        }
        this.modelChange = false;
      })
      .then(() => (this.isLoading = false));
  }

  private updatePercentages() {
    for (let tradeRecommendation of this.tradeRecommendations) {
      if (tradeRecommendation.cModelPercentage !== 0) {
        tradeRecommendation.targetModelAllocation = trunc(
          (100 * tradeRecommendation.targetMarketValue) / this.targetModelTotal,
          2
        );
      }

      // Possible alternative to the switch
      const assetClass = tradeRecommendation.security.assetClass.name;
      if (assetClass === AssetClassNames.CASH) {
        continue;
      }

      tradeRecommendation.targetAssetClassAllocation = trunc(
        (100 * tradeRecommendation.targetMarketValue) /
          this.getAssetClassTotal(this.targetAssetClass, assetClass),
        2
      );

      const totalCModelNAV = this.cModelTotal + this.totalAvailableCash();
      tradeRecommendation.targetCModelAllocation = trunc(
        (100 * tradeRecommendation.targetMarketValue) / totalCModelNAV,
        2
      );
    }
  }

  public onCashLimitUpdate() {
    // Only apply calculations if this is not due to some other change
    if (!this.modelChange) {
      this.modelChange = true;

      const cashToTrade = this.totalAvailableCash();
      const idealModelTotal = this.actualModelTotal + cashToTrade;

      this.tradeRecommendations
        .filter((trade) => trade.cModelPercentage !== 0)
        .forEach((trade) => {
          const idealMarketValue =
            (idealModelTotal * trade.cModelPercentage) / 100;
          const price = this.exchangeService.convertCurrency(
            trade.price,
            trade.security.currency,
            this.selectedCurrency
          );
          const idealQuantity = Math.floor(idealMarketValue / price);
          trade.idealBuySell = idealQuantity - trade.quantity;
        });

      this.updatePercentages();
      this.updateIdealCharts();

      this.modelChange = false;
    }
  }

  private totalAvailableCash() {
    const cashLimit =
      (this.assetClassesTotal * this.tradeSet.targetCashAllocation) / 100;
    const actualCashAvailable = this.assetClass.find(
      (a) => a.name === AssetClassNames.CASH
    ).total;
    return actualCashAvailable - cashLimit;
  }

  public onQuantityUpdate(tradeRecommendation: TradeRecommendation) {
    // Only apply calculations if this is not due to some other change
    if (!this.modelChange) {
      this.modelChange = true;

      if (!tradeRecommendation.buySellQuantity) {
        tradeRecommendation.buySellQuantity = 0;
      }

      let priceEstimate = this.exchangeService.convertCurrency(
        tradeRecommendation.price,
        tradeRecommendation.security.currency,
        this.selectedCurrency
      );
      tradeRecommendation.targetMarketValue = +(
        priceEstimate * tradeRecommendation.targetQuantity
      ).toFixed(2);
      const assetClass = tradeRecommendation.security.assetClass.name;
      if (
        assetClass == AssetClassNames.FIXED_INCOME &&
        tradeRecommendation.security.fixedIncomeType &&
        tradeRecommendation.security.fixedIncomeType.name ==
          FixedIncomeTypeNames.BOND
      ) {
        let otherCurrencyMarketValue =
          (tradeRecommendation.price * tradeRecommendation.targetQuantity) /
          100;
        let priceEstimate = this.exchangeService.convertCurrency(
          tradeRecommendation.price,
          tradeRecommendation.security.currency,
          "MUR"
        );
        tradeRecommendation.targetMarketValue =
          (otherCurrencyMarketValue * priceEstimate) / 100;
      }

      this.updateTargetCharts();
      this.updatePercentages();
      this.checkSecurityTrades(
        tradeRecommendation.security.id,
        tradeRecommendation
      );
      this.modelChange = false;
    }
  }

  public onModelAllocationUpdate(tradeRecommendation: TradeRecommendation) {
    // Only apply calculations if this is not due to some other change
    if (!this.modelChange) {
      this.modelChange = true;

      let targetMarketValue =
        (this.targetModelTotal * tradeRecommendation.targetModelAllocation) /
        100;
      tradeRecommendation.targetMarketValue = +targetMarketValue.toFixed(2);

      let priceEstimate = this.exchangeService.convertCurrency(
        tradeRecommendation.price,
        tradeRecommendation.security.currency,
        this.selectedCurrency
      );

      var targetQuantity = +(
        tradeRecommendation.targetMarketValue / priceEstimate
      ).toFixed(0);
      tradeRecommendation.buySellQuantity =
        targetQuantity - tradeRecommendation.quantity;

      this.updateTargetCharts();
      this.updatePercentages();

      this.modelChange = false;
    }
  }

  public onAssetClassAllocationUpdate(
    tradeRecommendation: TradeRecommendation
  ) {
    // Only apply calculations if this is not due to some other change
    if (!this.modelChange && tradeRecommendation.targetAssetClassAllocation) {
      this.modelChange = true;

      const assetClass = tradeRecommendation.security.assetClass.name;
      if (assetClass !== AssetClassNames.CASH) {
        const targetMarketValue = +(
          (this.getAssetClassTotal(this.targetAssetClass, assetClass) *
            tradeRecommendation.targetAssetClassAllocation) /
          100
        ).toFixed(2);
        tradeRecommendation.targetMarketValue = targetMarketValue;
      }

      let priceEstimate = this.exchangeService.convertCurrency(
        tradeRecommendation.price,
        tradeRecommendation.security.currency,
        this.selectedCurrency
      );

      var targetQuantity = +(
        tradeRecommendation.targetMarketValue / priceEstimate
      ).toFixed(0);
      tradeRecommendation.buySellQuantity =
        targetQuantity - tradeRecommendation.quantity;

      this.updateTargetCharts();
      this.updatePercentages();

      this.modelChange = false;
    }
  }

  public onCModelAllocationUpdate(tradeRecommendation: TradeRecommendation) {
    // Only apply calculations if this is not due to some other change
    if (!this.modelChange && tradeRecommendation.targetCModelAllocation) {
      this.modelChange = true;

      const assetClass = tradeRecommendation.security.assetClass.name;
      if (assetClass !== AssetClassNames.CASH) {
        const totalCModelNAV = this.cModelTotal + this.totalAvailableCash();
        tradeRecommendation.targetMarketValue = +(
          (totalCModelNAV * tradeRecommendation.targetCModelAllocation) /
          100
        ).toFixed(2);
      }

      let priceEstimate = this.exchangeService.convertCurrency(
        tradeRecommendation.price,
        tradeRecommendation.security.currency,
        this.selectedCurrency
      );

      const targetQuantity = +(
        tradeRecommendation.targetMarketValue / priceEstimate
      ).toFixed(0);
      tradeRecommendation.buySellQuantity =
        targetQuantity - tradeRecommendation.quantity;

      this.updateTargetCharts();
      this.updatePercentages();

      this.modelChange = false;
    }
  }

  public onLocalityChange() {
    this.updateActualCharts();
  }

  public matchIdealBuySell(tradeRecommendation: TradeRecommendation) {
    tradeRecommendation.buySellQuantity = tradeRecommendation.idealBuySell;
    this.onQuantityUpdate(tradeRecommendation);
  }

  public isTradableAssetClass(security: SecurityDetails) {
    return security.assetClass.name !== AssetClassNames.FIXED_INCOME;
  }

  public isSelectedLocality(security: SecurityDetails) {
    return security.locality === this.tradeLocality;
  }

  public showCashAccountBalances() {
    if (!this.tradeSet) return;

    let dialogScope = this.$rootScope.$new() as any;
    dialogScope.cashAccounts = this.tradeSet.currencyBalances;
    dialogScope.buySellDetails = this.tradeRecommendations
      .filter(
        (tr) =>
          tr.security.locality === this.tradeLocality && !!tr.buySellQuantity
      )
      .reduce((prev, tr) => {
        if (!prev[tr.security.currency]) {
          prev[tr.security.currency] = 0;
        }
        let trIncrease = tr.buySellQuantity * tr.price;
        if (
          tr.security.assetClass.name == AssetClassNames.FIXED_INCOME &&
          tr.security.fixedIncomeType &&
          tr.security.fixedIncomeType.name == FixedIncomeTypeNames.BOND
        ) {
          trIncrease = trIncrease / 100;
        }

        prev[tr.security.currency] += trIncrease;
        return prev;
      }, {});

    this.$mdDialog.show({
      template:
        '<cash-balance-dialog cash-accounts="cashAccounts" buy-sell-details="buySellDetails"></cash-balance-dialog>',
      scope: dialogScope,
    });
  }

  private submitSimulation() {
    this.isLoading = true;
    this.tradeSet.trades = this.tradeRecommendations.map((tr) => new Trade(tr));
    this.tradingRepo
      .saveTrades(
        this.clientId,
        this.portfolioId,
        this.tradeLocality,
        this.tradeSet
      )
      .then(() => this.onPortfolioSelect(this.activePortfolio))
      .catch((err) => this.notifyjs(err.data))
      .finally(() => (this.isLoading = false));
  }

  public saveTrades() {
    if (this.getTotalCash() < 0) {
      var confirmationConfig = this.$mdDialog
        .confirm()
        .title("Cash Exceeded!")
        .textContent(
          "Are you sure you would like to save this simulation, exceeding the current cash available for this portfolio?"
        )
        .ariaLabel(
          "Are you sure you would like to save this simulation, exceeding the current cash available for this portfolio?"
        )
        .ok("Yes, continue")
        .cancel("Cancel");

      this.$mdDialog
        .show(confirmationConfig)
        .then(() => this.submitSimulation());
    } else {
      this.submitSimulation();
    }
  }

  public cancelChanges() {
    this.modelChange = true;
    this.tradeSet = copy(this.tradeSetSnapshot);
    this.tradeRecommendations = this.tradeSet.trades.map(
      (t) => new TradeRecommendation(t)
    );
    this.updateTargetCharts();
    this.updateIdealCharts();
    this.activeSimulation = false;
  }

  public enableWatchers() {
    this.modelChange = false;
  }

  public disableSave() {
    if (!this.activePortfolio.isManaged) {
      return true;
    }
    if (!this.tradeRecommendations) {
      return false;
    }
    return this.tradeRecommendations.some(
      (r) =>
        !r.recommendationIsValid(
          this.targetAssetClassesTotal,
          this.targetAssetClass
        ) ||
        r.targetQuantity < 0 ||
        r.sellAllowed == true
    );
  }

  private updateActualCharts() {
    this.actualModelTotal = this.tradeRecommendations
      .filter((tr) => tr.cModelPercentage !== 0)
      .reduce((total, tr) => tr.marketValueDisplayCurrency + total, 0);

    this.assetClass = [
      {
        name: AssetClassNames.EQUITY,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) => tr.security.assetClass.name === AssetClassNames.EQUITY
            )
            .reduce((total, tr) => total + tr.marketValueDisplayCurrency, 0),
          4
        ),
      },
      {
        name: AssetClassNames.ALTERNATIVE_INV,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) =>
                tr.security.assetClass.name === AssetClassNames.ALTERNATIVE_INV
            )
            .reduce((total, tr) => total + tr.marketValueDisplayCurrency, 0),
          4
        ),
      },
      {
        name: AssetClassNames.FIXED_INCOME,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) =>
                tr.security.assetClass.name === AssetClassNames.FIXED_INCOME
            )
            .reduce((total, tr) => total + tr.marketValueDisplayCurrency, 0),
          4
        ),
      },
      {
        name: AssetClassNames.PRECIOUS_METALS,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) =>
                tr.security.assetClass.name === AssetClassNames.PRECIOUS_METALS
            )
            .reduce((total, tr) => total + tr.marketValueDisplayCurrency, 0),
          4
        ),
      },
      {
        name: AssetClassNames.CASH,
        total: trunc(
          this.cashItems.reduce(
            (total, c) => c.marketValueDisplayCurrency + total,
            0
          ),
          4
        ),
      },
    ];

    this.assetClassesTotal = this.assetClass.reduce(
      (prev, next) => prev + next.total,
      0
    );

    // this.tradeSet.targetPreciousMetalAllocation = (this.assetClass.find(i => i.name === AssetClassNames.PRECIOUS_METALS).total / this.assetClassesTotal) * 100
    // this.tradeSet.targetAlternativesAllocation = (this.assetClass.find(i => i.name === AssetClassNames.ALTERNATIVE_INVESTMENTS).total / this.assetClassesTotal) * 100
  }

  private updateTargetCharts() {
    this.totalBuy = 0;
    this.totalSell = 0;
    let netBuySell = this.tradeRecommendations
      .filter((tr) => tr.security.locality === this.tradeLocality)
      .reduce((total, tr) => {
        const diff = tr.buySellQuantity;
        if (diff === 0) {
          return total;
        }

        var val =
          this.exchangeService.convertCurrency(
            tr.price,
            tr.security.currency,
            this.selectedCurrency
          ) * diff;
        if (
          tr.security.fixedIncomeType &&
          tr.security.fixedIncomeType.name == FixedIncomeTypeNames.BOND
        ) {
          val = val / 100;
        }
        if (diff < 0) {
          this.totalSell += val;
        } else if (diff > 0) {
          this.totalBuy += val;
        }
        return val + total;
      }, 0);

    this.cModelTotal = this.tradeRecommendations
      .filter((tr) => tr.cModelPercentage !== 0)
      .reduce((total, tr) => tr.marketValueDisplayCurrency + total, 0);

    this.targetModelTotal = this.tradeRecommendations
      .filter((tr) => tr.cModelPercentage !== 0)
      .reduce((total, tr) => tr.targetMarketValue + total, 0);

    this.targetAssetClass = [
      {
        name: AssetClassNames.EQUITY,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) => tr.security.assetClass.name === AssetClassNames.EQUITY
            )
            .reduce((total, tr) => total + tr.targetMarketValue, 0),
          4
        ),
      },
      {
        name: AssetClassNames.ALTERNATIVE_INV,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) =>
                tr.security.assetClass.name === AssetClassNames.ALTERNATIVE_INV
            )
            .reduce((total, tr) => total + tr.targetMarketValue, 0),
          4
        ),
      },
      {
        name: AssetClassNames.FIXED_INCOME,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) =>
                tr.security.assetClass.name === AssetClassNames.FIXED_INCOME
            )
            .reduce((total, tr) => total + tr.targetMarketValue, 0),
          4
        ),
      },
      {
        name: AssetClassNames.PRECIOUS_METALS,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) =>
                tr.security.assetClass.name === AssetClassNames.PRECIOUS_METALS
            )
            .reduce((total, tr) => total + tr.targetMarketValue, 0),
          4
        ),
      },
      {
        name: AssetClassNames.CASH,
        total: trunc(
          this.cashItems.reduce(
            (total, c) => c.marketValueDisplayCurrency + total,
            0
          ) - netBuySell,
          4
        ),
      },
    ];

    this.targetAssetClassesTotal = this.targetAssetClass.reduce(
      (prev, next) => prev + next.total,
      0
    );
  }

  private updateIdealCharts() {
    const netBuySell = this.tradeRecommendations
      .filter((tr) => tr.security.locality === this.tradeLocality)
      .reduce((total, tr) => {
        const diff = tr.idealBuySell;
        if (diff === 0) {
          return total;
        }

        const val =
          this.exchangeService.convertCurrency(
            tr.price,
            tr.security.currency,
            this.selectedCurrency
          ) * diff;
        return val + total;
      }, 0);

    this.idealModelTotal = this.tradeRecommendations
      // .filter(tr => tr.security.locality === Locality.Local)
      .filter((tr) => tr.cModelPercentage !== 0)
      .reduce((total, tr) => tr.marketValueDisplayCurrency + total, netBuySell);

    const alternativeDiff = 0;
    const preciousMetalsDiff = 0;
    this.idealAssetClass = [
      {
        name: AssetClassNames.EQUITY,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) => tr.security.assetClass.name === AssetClassNames.EQUITY
            )
            .reduce(
              (total, tr) => total + tr.marketValueDisplayCurrency,
              netBuySell
            ),
          4
        ),
      },
      {
        name: AssetClassNames.ALTERNATIVE_INV,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) =>
                tr.security.assetClass.name === AssetClassNames.ALTERNATIVE_INV
            )
            .reduce(
              (total, tr) => total + tr.marketValueDisplayCurrency,
              alternativeDiff
            ),
          4
        ),
      },
      {
        name: AssetClassNames.FIXED_INCOME,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) =>
                tr.security.assetClass.name === AssetClassNames.FIXED_INCOME
            )
            .reduce((total, tr) => total + tr.marketValueDisplayCurrency, 0),
          4
        ),
      },
      {
        name: AssetClassNames.PRECIOUS_METALS,
        total: trunc(
          this.tradeRecommendations
            .filter(
              (tr) =>
                tr.security.assetClass.name === AssetClassNames.PRECIOUS_METALS
            )
            .reduce(
              (total, tr) => total + tr.marketValueDisplayCurrency,
              preciousMetalsDiff
            ),
          4
        ),
      },
      {
        name: AssetClassNames.CASH,
        total: trunc(
          this.cashItems.reduce(
            (total, c) => c.marketValueDisplayCurrency + total,
            0
          ) +
            alternativeDiff +
            preciousMetalsDiff -
            netBuySell,
          4
        ),
      },
    ];

    this.colourArray = this.idealAssetClass
      .filter((a) => a.total)
      .map((a) => {
        if (a.name == AssetClassNames.PRECIOUS_METALS) {
          return AssetClassColour["PRECIOUS_METALS"];
        }
        return AssetClassColour[a.name.replace(" ", "_").toUpperCase()];
      });
    this.idealAssetClassesTotal = this.idealAssetClass.reduce(
      (prev, next) => prev + next.total,
      0
    );
  }

  public getTotalCash() {
    if (!this.targetAssetClass) {
      return 0;
    }
    const cashAsset = this.targetAssetClass.find(
      (a) => a.name === AssetClassNames.CASH
    );
    if (!cashAsset) {
      return 0;
    }
    return cashAsset.total;
  }

  public showRestrictionDetail(tradeRestriction: TradeRestriction) {
    if (!tradeRestriction) {
      return;
    }

    return `Active: ${tradeRestriction.enabled ? "Yes" : "No"}
Restriction: ${tradeRestriction.restriction()}
Type: ${tradeRestriction.type()}
Created By: ${tradeRestriction.createdBy ? tradeRestriction.createdBy.name : "N/A"}
Date Created: ${new Date(tradeRestriction.dateCreated)}`;
  }

  public getAssetClassPieChartColours(item) {
    const itemInPieChart =
      AssetClassColourArray[AssetClassNameArray.indexOf(item.name)];
    if (itemInPieChart) {
      return itemInPieChart;
    } else {
      return "white";
    }
  }

  public getAssetClassTotal(assetClassTotals, assetClassName) {
    let assetClassTotal = assetClassTotals.find(
      (assetClass) => assetClass.name == assetClassName
    );
    return isNullOrUndefined(assetClassTotal) ? 0 : assetClassTotal.total;
  }

  public showAvailableSecurity() {
    const scope = this.$scope.$new() as any;
    scope.locality = this.tradeLocality;
    scope.filteredSecurities = this.tradeRecommendations.map(
      (tr) => tr.security
    );
    scope.portfolioRestrictions = [];
    const portfolioRestrictionsPromise = this.tradingRepo.getPortfolioTradeRestrictions(this.portfolioId).then((res) => {
        scope.portfolioRestrictions.push(res.data);
      });
    this.$mdDialog
      .show({
        template:
          '<security-dialog locality="locality" filtered-securities="filteredSecurities"></security-dialog>',
        clickOutsideToClose: false,
        escapeToClose: false,
        scope: scope,
      })
      .then(
        (selectedSecurity) =>
          new TradeRecommendation({
            price: selectedSecurity.lastPrice,
            security: selectedSecurity,
            restriction: scope.portfolioRestrictions.length > 0 ? scope.portfolioRestrictions[0].find(elem => elem.security.id == selectedSecurity.id) : ""
          })
      )
      .then((tradeRecommendation) => {
        // The restriction object is added as the TradeRecommendation class is instantiated so the following is no longer needed

        // let foundRestrictions = this.tradeRecommendations.filter((element) => {
        //   return (
        //     element.restriction &&
        //     element.restriction.assetClass &&
        //     tradeRecommendation.security.assetClass.name ==
        //       element.restriction.assetClass.name
        //   );
        // });
        // if (foundRestrictions.length > 0) {
        //   tradeRecommendation.restriction = foundRestrictions[0]
        //     .restriction as TradeRestriction;
        // }

        this.tradeRecommendations.push(tradeRecommendation);
        if (
          this.recommendationGrouping[
            tradeRecommendation.security.assetClass.name
          ]
        ) {
          this.recommendationGrouping[
            tradeRecommendation.security.assetClass.name
          ].push(tradeRecommendation);
        } else if (
          tradeRecommendation.security.assetClass.name ===
            AssetClassNames.FIXED_INCOME &&
          tradeRecommendation.security.fixedIncomeType &&
          this.recommendationGrouping[
            tradeRecommendation.security.fixedIncomeType.name
          ]
        ) {
          this.recommendationGrouping[
            tradeRecommendation.security.fixedIncomeType.name
          ].push(tradeRecommendation);
        }
        // this.recommendationGrouping[
        //   tradeRecommendation.security.assetClass.name
        // ].push(tradeRecommendation);
      })
      .catch(() => console.log("Dialog closed"));
  }

  showGroupingAvailableSecurity(grouping) {
    const scope = this.$scope.$new() as any;
    scope.locality = this.tradeLocality;
    scope.groupPortfolioRestrictions = [];
    const groupPortfolioRestrictionsPromise = this.tradingRepo.getPortfolioTradeRestrictions(this.portfolioId).then((res) => {
      scope.groupPortfolioRestrictions.push(res.data);
    });
    scope.filteredSecurities = this.tradeRecommendations.map(
      (tr) => tr.security
    );
    scope.specificAssetClass = grouping;

    this.$mdDialog
      .show({
        template:
          '<security-dialog locality="locality" filtered-securities="filteredSecurities" specific-asset-class="specificAssetClass"></security-dialog>',
        clickOutsideToClose: false,
        escapeToClose: false,
        scope: scope,
      })
      .then(
        (selectedSecurity) =>
          new TradeRecommendation({
            price: selectedSecurity.lastPrice,
            security: selectedSecurity,
            restriction: scope.groupPortfolioRestrictions.length > 0 ? scope.groupPortfolioRestrictions[0].find(elem => elem.security.id == selectedSecurity.id) : ""
          })
      )
      .then((tradeRecommendation) => {
        // The restriction object is added as the TradeRecommendation class is instantiated so the following is no longer needed
        
        // let foundRestrictions = this.tradeRecommendations.filter((element) => {
        //   return (
        //     element.restriction &&
        //     element.restriction.assetClass &&
        //     tradeRecommendation.security.assetClass.name ==
        //       element.restriction.assetClass.name
        //   );
        // });
        // if (foundRestrictions.length > 0) {
        //   tradeRecommendation.restriction = foundRestrictions[0]
        //     .restriction as TradeRestriction;
        // }
        this.tradeRecommendations.push(tradeRecommendation);
        if (
          this.recommendationGrouping[
            tradeRecommendation.security.assetClass.name
          ]
        ) {
          this.recommendationGrouping[
            tradeRecommendation.security.assetClass.name
          ].push(tradeRecommendation);
        } else if (
          tradeRecommendation.security.assetClass.name ===
            AssetClassNames.FIXED_INCOME &&
          tradeRecommendation.security.fixedIncomeType &&
          this.recommendationGrouping[
            tradeRecommendation.security.fixedIncomeType.name
          ]
        ) {
          this.recommendationGrouping[
            tradeRecommendation.security.fixedIncomeType.name
          ].push(tradeRecommendation);
        }
      })
      .catch((err) => {
        console.log("Dialog closed");
        console.log(err);
      });
  }

  public checkIfBond(security, grouping) {
    return (
      security.security.fixedIncomeType &&
      security.security.fixedIncomeType.name == grouping
    );
  }
}

export const portfolioTradeRecommendations: angular.IComponentOptions = {
  template: require("./portfolioAdmin.tradeRecommendations.html"),
  controller: TradeRecommendationsController,
  bindings: {
    clientId: "<",
    clientNumber: "<",
    selectedPortfolioIds: "<",
    selectedCurrency: "<",
    clientPortfolios: "<",
  },
};



// WEBPACK FOOTER //
// ./src/app/portfolioadmin/tradeRecommendations/portfolioAdmin.tradeRecommendations.ts